import { API_BASE, apiFetch } from '../config/apiBase';
/**
 * Client Profile Service
 * API helpers for client profile management
 */

const BASE = API_BASE;

/**
 * Get profile readiness score and missing fields
 * @param {string} clientId 
 * @returns {Promise<{success: boolean, score: number, missing: string[]}>}
 */
export async function getReadiness(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}/readiness`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Readiness check failed');
  }
  return json;
}

/**
 * Run profile generation (long-running 2-3 min)
 * @param {string} clientId 
 * @returns {Promise<{success: boolean, auditId: string, runId: string, score: number, tokensUsed: number, latencyMs: number, message: string}>}
 */
export async function runGeneration(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}/run`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: '{}'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Generation run failed');
  }
  return json;
}

/**
 * Get profile preview (markdown and HTML)
 * @param {string} clientId 
 * @returns {Promise<{markdown: string, html: string, generated: boolean, updated_at: string}>}
 */
export async function getPreview(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}/preview`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Preview fetch failed');
  }
  return json.data;
}

/**
 * Get audit history for profile generations
 * @param {string} clientId 
 * @param {number} limit - Max number of records (default 10)
 * @returns {Promise<Array<{auditId: string, timestamp: string, model: string, tokensUsed: number, latencyMs: number, readinessScore: number, success: boolean}>>}
 */
export async function getAuditHistory(clientId, limit = 10) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}/audits?limit=${limit}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to load audit history');
  }
  return json.data || [];
}

/**
 * Get real-time status of a running profile generation
 * Phase 4: Poll this every 2-3 seconds to get progress updates
 * @param {string} clientId 
 * @param {string} auditId 
 * @returns {Promise<{success: boolean, stage: string, progressPct: number, message: string, ...}>}
 */
export async function getRunStatus(clientId, auditId) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}/run/${auditId}/status`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Status check failed');
  }
  return json;
}

/**
 * Request cancellation of a running profile generation
 * Phase 4: Cooperative cancellation - stops at next checkpoint
 * @param {string} clientId 
 * @param {string} auditId 
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function cancelRun(clientId, auditId) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}/run/${auditId}/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: '{}'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Cancel failed');
  }
  return json;
}

// ============================================================================
// PHASE 5: VALIDATION SERVICES
// ============================================================================

/**
 * Validate NPI against NPPES registry
 * Phase 5: Real-time NPI validation with organization name lookup
 * @param {string} npi - 10-digit NPI number
 * @returns {Promise<{valid: boolean, name?: string, entityType?: string}>}
 */
export async function validateNpi(npi) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/validate/npi/${encodeURIComponent(npi)}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'NPI validation failed');
  }
  return json.data; // { valid: bool, name?: string, entityType?: 'organization'|'individual' }
}

/**
 * Validate and normalize address
 * Phase 5: Address validation with Google Maps API fallback
 * @param {string} addressQuery - Full address string
 * @returns {Promise<{valid: boolean, street?: string, city?: string, state?: string, zip?: string, normalized?: string}>}
 */
export async function validateAddress(addressQuery) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/validate/address?q=${encodeURIComponent(addressQuery)}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Address validation failed');
  }
  return json.data; // { valid: bool, street, city, state, zip, normalized?: string }
}

// ============================================================================
// PHASE 6: TEMPLATE/DEFAULTS SERVICES
// ============================================================================

/**
 * Get organization type defaults from field registry
 * Phase 6: Auto-apply defaults based on organization type selection
 * @param {string} orgType - Organization type (e.g., "Health System", "Hospital")
 * @param {string} variant - Optional variant (e.g., "small", "large") - for future use
 * @returns {Promise<Object>} - Defaults object { beds: 5000, service_lines: [...], ... }
 */
export async function getOrgDefaults(orgType, variant = null) {
  const token = localStorage.getItem('dva_token');
  const url = `${BASE}/api/v2/client-profile/template/${encodeURIComponent(orgType)}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
  
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  
  const json = await response.json();
  
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to load organization defaults');
  }
  
  return json.data || {}; // Returns flat object: { beds: 5000, service_lines: [...], ... }
}

/**
 * Get complete client profile data
 * @param {string} clientId 
 * @returns {Promise<Object>} - Complete profile object
 */
export async function getClientProfile(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await apiFetch(`${BASE}/api/v2/client-profile/${clientId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to load client profile: ${response.statusText}`);
  }
  
  const json = await response.json();
  
  // Handle different response formats
  if (json?.success && json?.data) {
    return json.data;
  } else if (json?.client_id || json?.organization_name) {
    return json;
  } else {
    throw new Error('Invalid profile response format');
  }
}